import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Statistic,
  Table,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Spin,
  Alert,
  Empty,
  Tabs,
  Tooltip,
  Badge,
  Divider,
  Descriptions,
} from "antd";
import {
  UserOutlined,
  ShopOutlined,
  DollarOutlined,
  PercentageOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TableOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import { getCrossMerchantAnalyticsFromServer } from "./crossMerchantAnalyticsApi";
import {
  CrossMerchantAnalytics,
  CrossMerchantConsumer,
} from "../../interfaces/crossMerchantAnalytics.interface";
import styles from "./CrossMerchantAnalytics.module.css";
import CrossMerchantMapView from "./components/CrossMerchantMapView";
import type { TabsProps } from "antd";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

export default function CrossMerchantAnalyticsDashboard() {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  const [data, setData] = useState<CrossMerchantAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Data collection started on Dec 5, 2025
  const DATA_COLLECTION_START_DATE = dayjs("2025-12-05");
  const today = dayjs();
  
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    DATA_COLLECTION_START_DATE, // Start from data collection start date
    today, // End today
  ]);
  const [minMerchants, setMinMerchants] = useState<number>(2);
  const [limit, setLimit] = useState<number>(100);
  const [activeTab, setActiveTab] = useState<string>("table");
  const [selectedConsumer, setSelectedConsumer] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        minMerchants: minMerchants || 2,
        limit: limit || 100,
      };

      if (dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const result = await getCrossMerchantAnalyticsFromServer(params);
      if (result) {
        setData(result);
      } else {
        setError("No data returned from server");
      }
    } catch (err: any) {
      console.error("Error fetching cross-merchant analytics:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load analytics data. Please try again.";
      setError(errorMessage);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    fetchData();
  };

  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY");
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (!date.isValid()) return "Invalid Date";
    return date.format("MMM DD, YYYY HH:mm");
  };

  // Get merchants with coordinates for map
  const merchantsWithCoordinates = useMemo(() => {
    if (!data?.consumers) return [];
    const allMerchants: Array<{
      consumer: CrossMerchantConsumer;
      merchant: CrossMerchantConsumer["merchants"][0];
    }> = [];
    data.consumers.forEach((consumer) => {
      consumer.merchants?.forEach((merchant) => {
        if (merchant.coordinates) {
          allMerchants.push({ consumer, merchant });
        }
      });
    });
    return allMerchants;
  }, [data]);

  const columns: ColumnsType<CrossMerchantConsumer> = [
    {
      title: "Payment Account Reference",
      dataIndex: "paymentAccountReference",
      key: "paymentAccountReference",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <Text code style={{ fontSize: "12px", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Merchants",
      dataIndex: "merchantCount",
      key: "merchantCount",
      width: 110,
      sorter: (a, b) => a.merchantCount - b.merchantCount,
      render: (count: number) => (
        <Tag color="blue" icon={<ShopOutlined />}>
          {count}
        </Tag>
      ),
    },
    {
      title: "Transactions",
      dataIndex: "totalTransactions",
      key: "totalTransactions",
      width: 130,
      sorter: (a, b) => a.totalTransactions - b.totalTransactions,
      render: (count: number) => (
        <Text strong>{count.toLocaleString()}</Text>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 160,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
      render: (amount: number) => (
        <Text strong style={{ color: "#3f8600" }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          icon={<GlobalOutlined />}
          onClick={() => {
            setSelectedConsumer(record.paymentAccountReference);
            setActiveTab("map");
          }}
        >
          View Map
        </Button>
      ),
    },
  ];

  const tabItems: TabsProps["items"] = [
    {
      key: "table",
      label: (
        <Space>
          <TableOutlined />
          <span>Table View</span>
        </Space>
      ),
      children: (
        <Card
          title={
            <Space>
              <UserOutlined />
              <span>Cross-Merchant Consumers</span>
              <Tag color="blue">
                {data?.consumers?.length || 0} of{" "}
                {data?.summary.totalCrossMerchantConsumers || 0}
              </Tag>
            </Space>
          }
          className={styles.table_card}
        >
                <Table
                  columns={columns}
                  dataSource={
                    data?.consumers && data.consumers.length > 0
                      ? data.consumers.map((consumer, index) => ({
                          ...consumer,
                          key:
                            consumer.paymentAccountReference || `consumer-${index}`,
                        }))
                      : []
                  }
                  loading={loading}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} consumers`,
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        description="No cross-merchant consumers found"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    ),
                  }}
            expandable={{
              expandedRowRender: (record) => {
                if (!record.merchants || record.merchants.length === 0) {
                  return (
                    <div style={{ padding: 16 }}>
                      <Text type="secondary">No merchant data available</Text>
                    </div>
                  );
                }
                return (
                  <div style={{ margin: 0 }}>
                    <Text strong className={styles.expanded_title}>
                      Merchant Breakdown:
                    </Text>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      {record.merchants.map((merchant, idx) => {
                        // Mobile: Use Descriptions component for compact layout
                        if (isMobile) {
                          return (
                            <div key={idx} className={styles.mobile_merchant_item}>
                              <div className={styles.mobile_merchant_header}>
                                <Space>
                                  <ShopOutlined />
                                  <Text strong>{merchant.businessTradeName}</Text>
                                  {merchant.city && (
                                    <Tag icon={<EnvironmentOutlined />}>
                                      {merchant.city}
                                    </Tag>
                                  )}
                                </Space>
                                {merchant.coordinates && (
                                  <Button
                                    type="link"
                                    size="small"
                                    icon={<GlobalOutlined />}
                                    onClick={() => {
                                      setSelectedConsumer(record.paymentAccountReference);
                                      setActiveTab("map");
                                    }}
                                    style={{ padding: 0 }}
                                  >
                                    Map
                                  </Button>
                                )}
                              </div>
                              <Descriptions
                                column={1}
                                size="small"
                                bordered={false}
                                labelStyle={{
                                  fontSize: "11px",
                                  width: "40%",
                                  padding: "4px 8px",
                                  color: "rgba(0, 0, 0, 0.45)",
                                }}
                                contentStyle={{
                                  fontSize: "12px",
                                  padding: "4px 8px",
                                }}
                              >
                                <Descriptions.Item label="Business Name">
                                  <Text strong>{merchant.businessName || "N/A"}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Transactions">
                                  <Text strong>{merchant.transactionCount || 0}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Total Amount">
                                  <Text strong style={{ color: "#3f8600" }}>
                                    {formatCurrency(merchant.totalAmount || 0)}
                                  </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Avg Transaction">
                                  {formatCurrency(merchant.averageTransactionValue || 0)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Visits">
                                  <Tag color="purple">
                                    {merchant.visitCount || 0}
                                  </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="First Visit">
                                  {merchant.firstVisit
                                    ? formatDateTime(merchant.firstVisit)
                                    : "N/A"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Last Visit">
                                  {merchant.lastVisit
                                    ? formatDateTime(merchant.lastVisit)
                                    : "N/A"}
                                </Descriptions.Item>
                                {merchant.coordinates && (
                                  <Descriptions.Item label="Coordinates">
                                    <Text code style={{ fontSize: "10px" }}>
                                      {merchant.coordinates.lat.toFixed(6)},{" "}
                                      {merchant.coordinates.lng.toFixed(6)}
                                    </Text>
                                  </Descriptions.Item>
                                )}
                                {merchant.address && (
                                  <Descriptions.Item label="Address">
                                    {[
                                      merchant.address.street,
                                      merchant.address.houseNumber,
                                      merchant.address.houseNumberSuffix,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    {merchant.address.postcode &&
                                      `, ${merchant.address.postcode}`}
                                    {merchant.address.city &&
                                      `, ${merchant.address.city}`}
                                  </Descriptions.Item>
                                )}
                              </Descriptions>
                            </div>
                          );
                        }
                        
                        // Desktop: Keep Card layout
                        return (
                          <Card
                            key={idx}
                            size="small"
                            className={styles.expanded_card}
                            title={
                              <Space>
                                <ShopOutlined />
                                <Text strong>{merchant.businessTradeName}</Text>
                                {merchant.city && (
                                  <Tag icon={<EnvironmentOutlined />}>
                                    {merchant.city}
                                  </Tag>
                                )}
                              </Space>
                            }
                            extra={
                              merchant.coordinates && (
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<GlobalOutlined />}
                                  onClick={() => {
                                    setSelectedConsumer(record.paymentAccountReference);
                                    setActiveTab("map");
                                  }}
                                >
                                  View on Map
                                </Button>
                              )
                            }
                          >
                            <Row gutter={[16, 8]} className={styles.expanded_row}>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">Business Name:</Text>
                              <br />
                              <Text strong>{merchant.businessName || "N/A"}</Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">Transactions:</Text>
                              <br />
                              <Text strong>{merchant.transactionCount || 0}</Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">Total Amount:</Text>
                              <br />
                              <Text strong style={{ color: "#3f8600" }}>
                                {formatCurrency(merchant.totalAmount || 0)}
                              </Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">Avg Transaction:</Text>
                              <br />
                              <Text>
                                {formatCurrency(
                                  merchant.averageTransactionValue || 0
                                )}
                              </Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">
                                <ClockCircleOutlined /> Visit Frequency:
                              </Text>
                              <br />
                              <Tag color="purple">
                                {merchant.visitCount || 0}
                              </Tag>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">
                                <CalendarOutlined /> First Visit:
                              </Text>
                              <br />
                              <Text>
                                {merchant.firstVisit
                                  ? formatDateTime(merchant.firstVisit)
                                  : "N/A"}
                              </Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">
                                <CalendarOutlined /> Last Visit:
                              </Text>
                              <br />
                              <Text>
                                {merchant.lastVisit
                                  ? formatDateTime(merchant.lastVisit)
                                  : "N/A"}
                              </Text>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Text type="secondary">Coordinates:</Text>
                              <br />
                              {merchant.coordinates ? (
                                <Text code style={{ fontSize: "11px" }}>
                                  {merchant.coordinates.lat.toFixed(6)},{" "}
                                  {merchant.coordinates.lng.toFixed(6)}
                                </Text>
                              ) : (
                                <Text type="secondary">Not available</Text>
                              )}
                            </Col>
                            {merchant.address && (
                              <>
                                <Col xs={24} sm={12} md={12}>
                                  <Text type="secondary">Address:</Text>
                                  <br />
                                  <Text>
                                    {[
                                      merchant.address.street,
                                      merchant.address.houseNumber,
                                      merchant.address.houseNumberSuffix,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    {merchant.address.postcode &&
                                      `, ${merchant.address.postcode}`}
                                    {merchant.address.city &&
                                      `, ${merchant.address.city}`}
                                    {merchant.address.country &&
                                      `, ${merchant.address.country}`}
                                  </Text>
                                </Col>
                              </>
                            )}
                          </Row>
                          {merchant.transactions &&
                            merchant.transactions.length > 0 && (
                              <>
                                <Divider style={{ margin: "12px 0" }} />
                                <Text type="secondary" strong>
                                  Transaction Timeline:
                                </Text>
                                <div style={{ marginTop: 8 }}>
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: "100%" }}
                                  >
                                    {merchant.transactions
                                      .sort(
                                        (a, b) =>
                                          new Date(a.transactionTime).getTime() -
                                          new Date(b.transactionTime).getTime()
                                      )
                                      .map((tx, txIdx) => (
                                        <div
                                          key={txIdx}
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            padding: "4px 8px",
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: 4,
                                          }}
                                        >
                                          <Text type="secondary">
                                            {formatDateTime(tx.transactionTime)}
                                          </Text>
                                          <Text strong style={{ color: "#3f8600" }}>
                                            {formatCurrency(tx.amount)}
                                          </Text>
                                        </div>
                                      ))}
                                  </Space>
                                </div>
                              </>
                            )}
                          </Card>
                        );
                      })}
                    </Space>
                  </div>
                );
              },
              rowExpandable: (record) =>
                record.merchants && record.merchants.length > 0,
            }}
          />
        </Card>
      ),
    },
    {
      key: "map",
      label: (
        <Space>
          <GlobalOutlined />
          <span>Map View</span>
          {merchantsWithCoordinates.length > 0 && (
            <Badge
              count={merchantsWithCoordinates.length}
              style={{ backgroundColor: "#52c41a" }}
            />
          )}
        </Space>
      ),
      children: (
        <CrossMerchantMapView
          data={data}
          selectedConsumer={selectedConsumer}
          onConsumerSelect={setSelectedConsumer}
          loading={loading}
        />
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2}>Cross-Merchant Consumer Analytics</Title>
        <Text type="secondary">
          Discover consumers who shop across multiple merchants in our network.
          Explore geographic patterns and shopping journeys.
        </Text>
      </div>

      <Card className={styles.filters_card}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Date Range:</Text>
              <br />
              <RangePicker
                value={dateRange}
                onChange={(dates) =>
                  setDateRange([dates?.[0] || null, dates?.[1] || null])
                }
                style={{ width: "100%", marginTop: 8 }}
                format="YYYY-MM-DD"
                disabledDate={(current) => {
                  // Disable dates before data collection started (Dec 5, 2025)
                  if (!current) return false;
                  return current.isBefore(DATA_COLLECTION_START_DATE, "day");
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Min Merchants:</Text>
              <br />
              <InputNumber
                min={2}
                max={100}
                value={minMerchants}
                onChange={(value) => setMinMerchants(value || 2)}
                style={{ width: "100%", marginTop: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text strong>Limit:</Text>
              <br />
              <InputNumber
                min={1}
                max={1000}
                value={limit}
                onChange={(value) => setLimit(value || 100)}
                style={{ width: "100%", marginTop: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading}
                style={{ marginTop: 32, width: "100%" }}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 24 }}
        />
      )}

      {loading && !data ? (
        <div className={styles.loading_container}>
          <Spin size="large" />
        </div>
      ) : data ? (
        <>
          <Row gutter={16} className={styles.stats_row}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Cross-Merchant Consumers"
                  value={data.summary.totalCrossMerchantConsumers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                  loading={loading}
                />
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  out of{" "}
                  {(data.summary.totalConsumers || 0).toLocaleString()} total
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Cross-Merchant Percentage"
                  value={data.summary.crossMerchantPercentage}
                  precision={2}
                  prefix={<PercentageOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Transactions"
                  value={data.summary.totalTransactions}
                  prefix={<ShopOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Amount"
                  value={data.summary.totalAmount}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: "#3f8600" }}
                  loading={loading}
                  formatter={(value) => formatCurrency(Number(value))}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} className={styles.stats_row}>
            <Col xs={24} sm={12} md={12}>
              <Card>
                <Statistic
                  title="Avg Merchants per Consumer"
                  value={data.summary.averageMerchantsPerConsumer}
                  precision={2}
                  prefix={<ShopOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                  loading={loading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Card>
                <Statistic
                  title="Avg Transactions per Consumer"
                  value={data.summary.averageTransactionsPerConsumer}
                  precision={2}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                  loading={loading}
                />
              </Card>
            </Col>
          </Row>

          <Card className={styles.content_card}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              type="card"
              size="large"
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}
